/**
 * Convert leaked LaTeX / math markup into spoken-ish Chinese for off-screen TTS & glance.
 * Not a full TeX parser — covers common KP fragments (frac/sqrt/sup/sub/ops/greek).
 */

const DIGIT_ZH = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'] as const;

function speakSmallNumber(raw: string): string | null {
  const t = String(raw || '').replace(/\s+/g, '').trim();
  if (!/^\d{1,3}$/.test(t)) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || n > 99) {
    if (t.length === 1) return DIGIT_ZH[Number(t)] || t;
    return t.split('').map((d) => DIGIT_ZH[Number(d)] || d).join('');
  }
  if (n <= 10) return n === 10 ? '十' : DIGIT_ZH[n];
  if (n < 20) return `十${DIGIT_ZH[n % 10]}`;
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return ones === 0 ? `${DIGIT_ZH[tens]}十` : `${DIGIT_ZH[tens]}十${DIGIT_ZH[ones]}`;
}

function unwrapBraced(inner: string): string {
  const t = String(inner || '').trim();
  if (t.startsWith('{') && t.endsWith('}')) {
    return t.slice(1, -1);
  }
  return t;
}

function speakAtom(raw: string): string {
  let t = String(raw || '').trim();
  if (!t) return '';
  const asNum = speakSmallNumber(unwrapBraced(t));
  if (asNum) return asNum;
  // Recurse lightly on nested commands still present.
  t = latexChunkToSpoken(t);
  return t.replace(/\s+/g, ' ').trim();
}

function speakSubscript(raw: string): string {
  const inner = unwrapBraced(raw);
  const asNum = speakSmallNumber(inner);
  if (asNum) return asNum;
  const spoken = speakAtom(inner);
  return spoken || inner;
}

function latexChunkToSpoken(source: string): string {
  let text = String(source || '');

  // \text{...} / \mathrm{...} → keep inner
  text = text.replace(
    /\\(?:text|textrm|textit|textbf|mathrm|mathbf|mathsf|mathtt|mbox|operatorname)\*?\{([^{}]*)\}/g,
    (_m, inner: string) => ` ${inner} `,
  );

  // \frac{a}{b} → 二分之一 (prefer natural Chinese for small ints)
  for (let pass = 0; pass < 8; pass += 1) {
    const next = text.replace(/\\frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, (_m, a: string, b: string) => {
      const num = speakAtom(a);
      const den = speakAtom(b);
      if (!num || !den) return ' 分数 ';
      return ` ${den}分之${num} `;
    });
    if (next === text) break;
    text = next;
  }

  // \sqrt[n]{x} / \sqrt{x}
  text = text.replace(/\\sqrt\s*\[([^\]]*)\]\s*\{([^{}]*)\}/g, (_m, n: string, x: string) => {
    const root = speakAtom(n);
    const body = speakAtom(x);
    return root ? ` ${root}次根号${body} ` : ` 根号${body} `;
  });
  text = text.replace(/\\sqrt\s*\{([^{}]*)\}/g, (_m, x: string) => ` 根号${speakAtom(x)} `);

  // ^{...} / _{...} and single-char
  text = text.replace(/\^\{([^{}]*)\}/g, (_m, exp: string) => {
    const e = speakAtom(exp);
    if (e === '二' || e === '2') return '的平方';
    if (e === '三' || e === '3') return '的立方';
    return `的${e}次方`;
  });
  text = text.replace(/\^([A-Za-z0-9])/g, (_m, exp: string) => {
    if (exp === '2') return '的平方';
    if (exp === '3') return '的立方';
    const e = speakSmallNumber(exp) || exp;
    return `的${e}次方`;
  });
  // a_1 / a_{n} → a一 / an（避免字面「下标」粘连）
  text = text.replace(/([A-Za-z])_\{([^{}]*)\}/g, (_m, base: string, sub: string) => `${base}${speakSubscript(sub)}`);
  text = text.replace(/([A-Za-z])_([A-Za-z0-9])/g, (_m, base: string, sub: string) => `${base}${speakSubscript(sub)}`);
  text = text.replace(/_\{([^{}]*)\}/g, (_m, sub: string) => ` ${speakSubscript(sub)} `);
  text = text.replace(/_([A-Za-z0-9])/g, (_m, sub: string) => ` ${speakSubscript(sub)} `);

  // Greek / symbols
  const symbolMap: Array<[RegExp, string]> = [
    [/\\alpha\b/g, '阿尔法'],
    [/\\beta\b/g, '贝塔'],
    [/\\gamma\b/g, '伽马'],
    [/\\delta\b/g, '德尔塔'],
    [/\\theta\b/g, '西塔'],
    [/\\pi\b/g, '派'],
    [/\\sigma\b/g, '西格玛'],
    [/\\mu\b/g, '缪'],
    [/\\infty\b/g, '无穷'],
    [/\\pm\b/g, '正负'],
    [/\\mp\b/g, '负正'],
    [/\\times\b/g, '乘'],
    [/\\cdot\b/g, '乘'],
    [/\\div\b/g, '除以'],
    [/\\leq\b|\\le\b/g, '小于等于'],
    [/\\geq\b|\\ge\b/g, '大于等于'],
    [/\\neq\b|\\ne\b/g, '不等于'],
    [/\\approx\b/g, '约等于'],
    [/\\equiv\b/g, '恒等于'],
    [/\\to\b/g, '趋向'],
    [/\\in\b/g, '属于'],
    [/\\notin\b/g, '不属于'],
    [/\\subset\b/g, '包含于'],
    [/\\cup\b/g, '并'],
    [/\\cap\b/g, '交'],
    [/\\triangle\b/g, '三角形'],
    [/\\angle\b/g, '角'],
    [/\\perp\b/g, '垂直'],
    [/\\parallel\b/g, '平行'],
    [/\\circ\b/g, '度'],
    [/\\degree\b/g, '度'],
    [/\\%|\b\\%/g, '百分之'],
  ];
  for (const [re, spoken] of symbolMap) {
    text = text.replace(re, spoken);
  }

  // Drop layout commands
  text = text
    .replace(/\\(?:left|right|big|Big|bigg|Bigg|bigl|bigr|Bigl|Bigr|biggl|biggr)\b/g, ' ')
    .replace(/\\(?:begin|end)\{[^{}]*\}/g, ' ')
    .replace(/\\(?:quad|qquad|hspace|vspace|phantom|vphantom|rule)\*?(\{[^{}]*\})*/g, ' ')
    .replace(/\\[a-zA-Z]+\*?/g, ' ');

  // Strip delimiters & braces leftovers
  text = text
    .replace(/\$\$/g, ' ')
    .replace(/\$/g, ' ')
    .replace(/\\\(|\\\)|\\\[|\\\]/g, ' ')
    .replace(/[{}]/g, ' ')
    .replace(/~/g, ' ')
    .replace(/&/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return text;
}

/** Extract math segments and convert whole string to spoken Chinese. */
export function latexToSpokenZh(raw: string): string {
  let text = String(raw || '')
    .replace(/\u000crac/g, '\\frac')
    .replace(/\\n/g, '\n')
    .trim();
  if (!text) return '';

  // Convert delimited math first
  text = text.replace(/\$\$([\s\S]+?)\$\$/g, (_m, inner: string) => ` ${latexChunkToSpoken(inner)} `);
  text = text.replace(/\$([^$\n]+?)\$/g, (_m, inner: string) => ` ${latexChunkToSpoken(inner)} `);
  text = text.replace(/\\\(([\s\S]+?)\\\)/g, (_m, inner: string) => ` ${latexChunkToSpoken(inner)} `);
  text = text.replace(/\\\[([\s\S]+?)\\\]/g, (_m, inner: string) => ` ${latexChunkToSpoken(inner)} `);

  // Remaining bare commands in prose
  if (/\\[a-zA-Z]|[_^]/.test(text)) {
    text = latexChunkToSpoken(text);
  }

  return text
    .replace(/\s+/g, ' ')
    .replace(/\s*([，。！？；：、])/g, '$1')
    // Soften leftover OCR-ish "下标" glue if any slipped through
    .replace(/([A-Za-z])下标\s*/g, '$1')
    .trim();
}

/** Compact one-line glance label from spoken text. */
export function compactSpokenGlance(spoken: string, maxLen = 18): string {
  const t = String(spoken || '').replace(/\s+/g, ' ').trim();
  if (!t) return '要点';
  const clause = t.split(/[，。？！；：、\n]/)[0] || t;
  if (clause.length <= maxLen) return clause;
  return truncateForGlance(clause, maxLen);
}

/** Truncate for glance body; prefer cutting at clause boundaries. */
export function truncateForGlance(spoken: string, maxLen = 160): string {
  const t = String(spoken || '').replace(/\s+/g, ' ').trim();
  if (t.length <= maxLen) return t;
  const slice = t.slice(0, maxLen);
  const markers = ['。', '？', '！', '；', '，', '、', ' '];
  let cut = -1;
  for (const m of markers) {
    const i = slice.lastIndexOf(m);
    if (i > cut) cut = i;
  }
  if (cut >= Math.floor(maxLen * 0.45)) {
    return slice.slice(0, cut + (slice[cut] === ' ' ? 0 : 1)).trim();
  }
  return `${slice.slice(0, maxLen - 1)}…`;
}
